import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { BookOpen, PenLine, Bot, Star, ChevronRight } from 'lucide-react'

interface Article {
  id: string
  title: string
  summary: string
  content: string
  tags: string[]
  createdAt: string
  ratingTotal: number
  ratingCount: number
}

interface ArticleFormState {
  title: string
  summary: string
  content: string
  tags: string
}

const STORAGE_KEY = 'portfolio_articles_v1'
const RATING_STORAGE_KEY = 'portfolio_article_user_ratings_v1'
const READ_STORAGE_KEY = 'portfolio_article_read_v1'

const seedArticles: Article[] = [
  {
    id: 'seed-1',
    title: 'Software Engineering Lifecycle in the AI Era',
    summary:
      'How planning, implementation, testing, deployment, and maintenance are evolving with AI-assisted workflows.',
    content:
      'AI tools are now present in every phase of the engineering lifecycle. Requirement clarification is faster, implementation is accelerated through assisted coding, and test generation is becoming more reliable.\n\nThe challenge is governance: teams need standards for review quality, traceability, security, and accountability when AI is used in production paths.',
    tags: ['AI', 'Engineering Lifecycle', 'Software Delivery'],
    createdAt: new Date().toISOString(),
    ratingTotal: 0,
    ratingCount: 0
  },
  {
    id: 'seed-2',
    title: 'From Feature Factories to Engineering Systems',
    summary:
      'Why modern teams need stronger technical systems to scale quality while shipping quickly.',
    content:
      'In an AI-accelerated environment, speed without architecture leads to instability. Strong systems thinking is now a competitive advantage: observability, modular boundaries, release discipline, and feedback loops.',
    tags: ['Architecture', 'DevOps', 'AI'],
    createdAt: new Date().toISOString(),
    ratingTotal: 0,
    ratingCount: 0
  }
]

const Articles = () => {
  const [articles, setArticles] = useState<Article[]>(seedArticles)
  const [userRatings, setUserRatings] = useState<Record<string, number>>({})
  const [readArticles, setReadArticles] = useState<Record<string, boolean>>({})
  const [formData, setFormData] = useState<ArticleFormState>({
    title: '',
    summary: '',
    content: '',
    tags: ''
  })
  const [status, setStatus] = useState<'idle' | 'saved'>('idle')

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) return
      const parsed = JSON.parse(stored) as Article[]
      if (Array.isArray(parsed) && parsed.length > 0) {
        setArticles(
          parsed.map((article) => ({
            ...article,
            ratingTotal: article.ratingTotal ?? 0,
            ratingCount: article.ratingCount ?? 0
          }))
        )
      }
    } catch {
      // Ignore malformed local cache and keep defaults.
    }

    try {
      const storedRatings = localStorage.getItem(RATING_STORAGE_KEY)
      if (storedRatings) {
        const parsedRatings = JSON.parse(storedRatings) as Record<string, number>
        setUserRatings(parsedRatings)
      }
    } catch {
      // Ignore malformed local cache.
    }

    try {
      const storedReadArticles = localStorage.getItem(READ_STORAGE_KEY)
      if (storedReadArticles) {
        const parsedReadArticles = JSON.parse(storedReadArticles) as Record<string, boolean>
        setReadArticles(parsedReadArticles)
      }
    } catch {
      // Ignore malformed local cache.
    }
  }, [])

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(articles))
  }, [articles])

  useEffect(() => {
    localStorage.setItem(RATING_STORAGE_KEY, JSON.stringify(userRatings))
  }, [userRatings])

  useEffect(() => {
    localStorage.setItem(READ_STORAGE_KEY, JSON.stringify(readArticles))
  }, [readArticles])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const tags = formData.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean)

    const newArticle: Article = {
      id: crypto.randomUUID(),
      title: formData.title.trim(),
      summary: formData.summary.trim(),
      content: formData.content.trim(),
      tags,
      createdAt: new Date().toISOString(),
      ratingTotal: 0,
      ratingCount: 0
    }

    setArticles((prev) => [newArticle, ...prev])
    setFormData({ title: '', summary: '', content: '', tags: '' })
    setStatus('saved')
    setTimeout(() => setStatus('idle'), 3000)
  }

  const handleReadToggle = (articleId: string, isOpen: boolean) => {
    if (!isOpen) return
    setReadArticles((prev) => ({ ...prev, [articleId]: true }))
  }

  const handleRateArticle = (articleId: string, rating: number) => {
    const previousRating = userRatings[articleId]

    setArticles((prev) =>
      prev.map((article) => {
        if (article.id !== articleId) return article

        if (previousRating) {
          return {
            ...article,
            ratingTotal: article.ratingTotal - previousRating + rating
          }
        }

        return {
          ...article,
          ratingTotal: article.ratingTotal + rating,
          ratingCount: article.ratingCount + 1
        }
      })
    )

    setUserRatings((prev) => ({ ...prev, [articleId]: rating }))
  }

  return (
    <section className="section section-border">
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="mb-12 max-w-2xl"
        >
          <span className="section-label mb-4">Articles</span>
          <h2 className="text-balance mb-6">Thoughts on engineering.</h2>
          <p className="text-lg text-zinc-500 dark:text-zinc-400">
            Notes on software delivery, systems thinking, and how AI is reshaping the engineering lifecycle.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="card lg:sticky lg:top-24">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-900">
                  <PenLine className="h-4 w-4 text-cyan-500" strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
                  Write an Article
                </h3>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="title" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-400">
                    Title
                  </label>
                  <input
                    id="title"
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Article title"
                  />
                </div>

                <div>
                  <label htmlFor="summary" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-400">
                    Summary
                  </label>
                  <textarea
                    id="summary"
                    name="summary"
                    required
                    rows={3}
                    value={formData.summary}
                    onChange={handleInputChange}
                    className="textarea"
                    placeholder="Short summary"
                  />
                </div>

                <div>
                  <label htmlFor="content" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-400">
                    Content
                  </label>
                  <textarea
                    id="content"
                    name="content"
                    required
                    rows={8}
                    value={formData.content}
                    onChange={handleInputChange}
                    className="textarea"
                    placeholder="Write your article here..."
                  />
                </div>

                <div>
                  <label htmlFor="tags" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-400">
                    Tags (comma-separated)
                  </label>
                  <input
                    id="tags"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="AI, DevOps, Architecture"
                  />
                </div>

                <button type="submit" className="w-full btn-primary">
                  Publish Article
                </button>

                {status === 'saved' && (
                  <p className="rounded-lg border border-cyan-100 bg-cyan-50 p-2.5 text-center text-sm text-cyan-700 dark:border-cyan-900 dark:bg-cyan-950/20 dark:text-cyan-400">
                    Article published successfully.
                  </p>
                )}
              </form>
            </div>
          </motion.div>

          <div className="space-y-5 lg:col-span-3">
            {articles.map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05, ease: [0, 0, 0.2, 1] }}
                viewport={{ once: true }}
                className="card"
              >
                {(() => {
                  const averageRating =
                    article.ratingCount > 0
                      ? (article.ratingTotal / article.ratingCount).toFixed(1)
                      : null

                  return (
                    <>
                      <div className="mb-4 flex flex-wrap items-center gap-2">
                        <span className="tag">
                          <BookOpen className="h-3 w-3" />
                          Article
                        </span>
                        <span className="tag">
                          <Bot className="h-3 w-3" />
                          AI Era
                        </span>
                        {averageRating ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-cyan-50 px-2 py-1 font-mono text-[10px] font-medium uppercase tracking-wide text-cyan-600 dark:bg-cyan-950/30 dark:text-cyan-400">
                            <Star className="h-3 w-3" />
                            {averageRating} ({article.ratingCount})
                          </span>
                        ) : null}
                        <span className="ml-auto text-xs text-zinc-400">
                          {new Date(article.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>

                      <h3 className="mb-2 text-lg font-semibold text-zinc-950 dark:text-zinc-50">
                        {article.title}
                      </h3>
                      <p className="mb-4 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
                        {article.summary}
                      </p>

                      <details
                        className="group"
                        onToggle={(e) =>
                          handleReadToggle(article.id, e.currentTarget.open)
                        }
                      >
                        <summary className="flex cursor-pointer list-none items-center gap-1 text-sm font-semibold text-cyan-500 transition-colors duration-200 hover:text-cyan-600 dark:hover:text-cyan-400">
                          Read full article
                          <ChevronRight
                            size={14}
                            strokeWidth={2}
                            className="transition-transform duration-200 group-open:rotate-90"
                          />
                        </summary>
                        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
                          {article.content}
                        </p>
                      </details>

                      <div className="mt-4">
                        {readArticles[article.id] ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="mr-1 text-xs text-zinc-500">Rate:</span>
                            {[1, 2, 3, 4, 5].map((value) => (
                              <button
                                key={`${article.id}-rating-${value}`}
                                type="button"
                                onClick={() => handleRateArticle(article.id, value)}
                                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors duration-200 ${
                                  userRatings[article.id] === value
                                    ? 'border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-800 dark:bg-cyan-950/30 dark:text-cyan-400'
                                    : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 dark:border-zinc-800 dark:bg-canvas-dark dark:text-zinc-400 dark:hover:border-zinc-700'
                                }`}
                              >
                                <Star className="h-3 w-3" />
                                {value}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-zinc-500">
                            Open and read the article to enable rating.
                          </p>
                        )}
                      </div>

                      {article.tags.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {article.tags.map((tag) => (
                            <span key={`${article.id}-${tag}`} className="tag">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  )
                })()}
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Articles
